import React, { useEffect, useState } from 'react';
import { api } from './api';
import { toast } from '../hooks/use-toast';
import { Gift, Copy, Loader2, Mail, MessageCircle, Linkedin, Trophy, CheckCircle2, Clock, RefreshCw, Crown, Medal } from 'lucide-react';
import { Input } from '../components/ui/input';
import { TableScroller } from '@/components/ui/table-scroller';
import { copyText } from './clipboard';
import { formatDate } from './kit/time';
import LongTextCell from '@/components/LongTextCell';

const STATUS = {
  pending:   { color: '#94a3b8', label: 'Invited',    icon: Clock },
  signed_up: { color: '#3b82f6', label: 'Signed up',  icon: CheckCircle2 },
  converted: { color: '#22c55e', label: 'Paid',       icon: Trophy },
};

export default function ReferralsPage() {
  const [data, setData] = useState(null);
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [me, lb] = await Promise.all([
        api.get('/referrals/my-referrals'),
        api.get('/referrals/leaderboard'),
      ]);
      setData(me.data);
      setBoard(lb.data);
    } catch (e) {
      toast({ title: 'Failed to load', description: e.response?.data?.detail || 'Try again' });
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading || !data) {
    return <div className="p-10 text-slate-500 flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Loading referrals…</div>;
  }

  const origin = window.location.origin;
  const link = `${origin}/portal/signup?ref=${data.code}`;
  const { kpis, referrals: rows } = data;

  const copyLink = async () => {
    if (await copyText(link)) toast({ title: 'Link copied', description: link });
    else toast({ title: 'Copy failed', description: 'Select the text and copy it manually' });
  };

  const shareVia = (channel) => {
    const subject = encodeURIComponent('You should try BrokerForYou');
    const body = encodeURIComponent(`I've been using BrokerForYou to run my brokerage — CRM, calculators, e-sign, AI, all in one place. Sign up with my link and we both get a month free:\n\n${link}`);
    if (channel === 'email') window.open(`mailto:?subject=${subject}&body=${body}`);
    if (channel === 'sms') window.open(`sms:?body=${body}`);
    if (channel === 'linkedin') window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(link)}`);
  };

  return (
    <div className="p-4 md:p-8 max-w-5xl mx-auto" data-testid="referrals-page">
      {/* Hero */}
      <div className="rounded-2xl p-6 md:p-8 mb-6 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #070B5F 0%, #0f1580 100%)' }}>
        <div className="absolute -right-10 -top-10 opacity-10 pointer-events-none"><Gift size={220} className="text-white" /></div>
        <div className="text-[10px] uppercase tracking-widest text-white/60">Refer-a-Broker Program</div>
        <div className="font-serif text-3xl md:text-4xl text-white mt-1">Give a month · Get a month</div>
        <div className="mt-2 text-white/70 text-sm max-w-xl">Share your unique link with another brokerage. When they upgrade to a paid plan, you both get <strong className="text-white">1 month free</strong> credited to your next invoice.</div>
      </div>

      {/* KPI row */}
      <div className="grid md:grid-cols-4 gap-3 mb-6">
        <KPI label="Invited"     value={kpis.total}                     color="#94a3b8" testid="kpi-total" />
        <KPI label="Signed Up"   value={kpis.signed_up}                 color="#3b82f6" testid="kpi-signed" />
        <KPI label="Converted"   value={kpis.converted}                 color="#22c55e" testid="kpi-converted" />
        <KPI label="Credit Earned" value={`${kpis.credit_months_earned} mo`} color="#8DC63F" testid="kpi-credit"
             note={kpis.credit_months_pending > 0 ? `+${kpis.credit_months_pending} mo awaiting credit` : null} />
      </div>

      {/* Share bar */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 mb-6" data-testid="share-bar">
        <div className="text-[10px] uppercase tracking-widest font-bold mb-2" style={{ color: '#6BA82E' }}>Your unique link</div>
        <div className="flex gap-2">
          <Input value={link} readOnly onFocus={(e) => e.target.select()} className="font-mono text-[13px]" data-testid="referral-link" />
          <button
            onClick={copyLink}
            data-testid="copy-link"
            className="inline-flex items-center gap-1.5 text-[12px] font-semibold px-4 py-2 rounded-lg text-white shadow-sm hover:brightness-110 shrink-0"
            style={{ background: '#070B5F' }}
          ><Copy size={13} /> Copy</button>
        </div>
        <div className="flex flex-wrap gap-2 mt-3">
          <ShareBtn onClick={() => shareVia('email')}    icon={Mail}         label="Email"    testid="share-email" />
          <ShareBtn onClick={() => shareVia('sms')}      icon={MessageCircle} label="Text"     testid="share-sms" />
          <ShareBtn onClick={() => shareVia('linkedin')} icon={Linkedin}      label="LinkedIn" testid="share-linkedin" />
          <div className="ml-auto text-[11px] text-slate-500 self-center font-mono">Code: <strong>{data.code}</strong></div>
        </div>
      </div>

      {/* Leaderboard — this month's top 10 referrers, social proof + ambassador driver */}
      {board?.top?.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm mb-6 overflow-hidden" data-testid="leaderboard">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
            <Crown size={14} style={{ color: '#f59e0b' }} />
            <div>
              <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: '#6BA82E' }}>Top Referrers · {board.month}</div>
              <div className="text-sm text-slate-500">Bragging rights for the crew who ships the most brokerages</div>
            </div>
          </div>
          <div className="divide-y divide-slate-100">
            {board.top.map((r) => {
              const medal = r.rank === 1 ? '🥇' : r.rank === 2 ? '🥈' : r.rank === 3 ? '🥉' : `#${r.rank}`;
              return (
                <div key={r.rank} className="px-5 py-3 flex items-center justify-between hover:bg-slate-50/60" data-testid={`leaderboard-row-${r.rank}`}
                     style={r.is_you ? { background: '#8DC63F10', borderLeft: '3px solid #8DC63F' } : {}}>
                  <div className="flex items-center gap-3">
                    <div className="text-lg w-8 text-center">{medal}</div>
                    <div>
                      <div className="font-semibold text-slate-800 text-[13px]">
                        {r.display_name}
                        {r.is_you && <span className="ml-2 text-[10px] uppercase tracking-widest font-bold" style={{ color: '#6BA82E' }}>You</span>}
                      </div>
                      <div className="text-[10px] text-slate-400">{r.conversions} conversion{r.conversions !== 1 ? 's' : ''}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-semibold text-[13px]" style={{ color: '#8DC63F' }}>+{r.credit_months} mo</div>
                    <div className="text-[10px] text-slate-400">earned</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Referrals list */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: '#6BA82E' }}>Your Referrals</div>
            <div className="text-sm text-slate-500">{rows.length} in flight</div>
          </div>
          <button onClick={load} data-testid="refresh-referrals" className="text-slate-400 hover:text-slate-700"><RefreshCw size={14} /></button>
        </div>
        {rows.length === 0 ? (
          <div className="p-10 text-center" data-testid="referrals-empty">
            <Gift size={28} className="mx-auto text-slate-300 mb-2" />
            <div className="text-sm font-semibold text-slate-700">No referrals yet</div>
            <div className="text-[12px] text-slate-500 mt-1">Share your link above — you'll see brokerages appear here as they sign up.</div>
          </div>
        ) : (
          <TableScroller><table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-100">
              <tr className="text-[10px] uppercase tracking-widest text-slate-500">
                <th className="text-left px-4 py-2.5 font-semibold">Invitee</th>
                <th className="text-left px-3 py-2.5 font-semibold">Status</th>
                <th className="text-left px-3 py-2.5 font-semibold">Invited</th>
                <th className="text-right px-3 py-2.5 font-semibold">Credit</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r) => {
                const s = STATUS[r.status] || STATUS.pending;
                const Icon = s.icon;
                return (
                  <tr key={r.referral_id} className="hover:bg-slate-50/60" data-testid={`referral-row-${r.referral_id}`}>
                    <td className="px-4 py-3 text-slate-700 font-mono text-[13px]">
                      <LongTextCell id={`referral-email-${r.referral_id}`} text={r.invitee_email || '—'} lines={1} maxWidth={240} />
                    </td>
                    <td className="px-3 py-3">
                      <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full" style={{ background: s.color + '20', color: s.color }}>
                        <Icon size={11} /> {s.label}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-[11px] text-slate-500">{formatDate(r.created_at)}</td>
                    <td className="px-3 py-3 text-right font-semibold" style={{ color: r.status === 'converted' ? '#8DC63F' : '#94a3b8' }}>
                      {r.status === 'converted' ? `+${r.credit_months || 1} mo` : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table></TableScroller>
        )}
      </div>
    </div>
  );
}

const KPI = ({ label, value, color, testid, note }) => (
  <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">{label}</div>
    <div className="font-serif text-3xl mt-1" style={{ color }}>{value}</div>
    {note && <div className="text-[11px] text-slate-400 mt-1" data-testid={`${testid}-note`}>{note}</div>}
  </div>
);

const ShareBtn = ({ onClick, icon: Icon, label, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className="inline-flex items-center gap-1.5 text-[12px] font-semibold px-3 py-1.5 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50"
  ><Icon size={12} /> {label}</button>
);
